/**
 * CREATIVE DETONATOR - UUID Utilities
 * Validation and helper functions for UUID/ID handling
 *
 * @version 1.0.0
 */

// ============================================================================
// UUID VALIDATION
// ============================================================================

/**
 * UUID v4 regex pattern
 * Matches: 8-4-4-4-12 hex characters (36 total with hyphens)
 * Example: 550e8400-e29b-41d4-a716-446655440000
 */
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID
 * @param {string} str - String to validate
 * @returns {boolean} True if valid UUID format
 *
 * @example
 * isValidUUID('550e8400-e29b-41d4-a716-446655440000') // true
 * isValidUUID('idea_1') // false
 * isValidUUID(null) // false
 */
export function isValidUUID(str) {
  if (!str || typeof str !== 'string') return false;
  return UUID_REGEX.test(str);
}

/**
 * Check if an ID looks like a database ID (vs temporary generator ID)
 * Database IDs are UUIDs from Supabase
 * Generator IDs are like "idea_1", "idea_2", etc.
 *
 * @param {string} id - ID to check
 * @returns {boolean} True if this appears to be a database ID
 *
 * @example
 * isDatabaseId('550e8400-e29b-41d4-a716-446655440000') // true
 * isDatabaseId('idea_1') // false
 * isDatabaseId('A1-12345') // false (but might be legacy format)
 */
export function isDatabaseId(id) {
  if (!id) return false;

  // Primary check: valid UUID
  if (isValidUUID(id)) return true;

  // Fallback: length check for backwards compatibility
  // Generator IDs are typically "idea_N" format (short)
  // Database UUIDs are 36 chars
  return id.length > 20 && !id.startsWith('idea_');
}

/**
 * Extract UUID from a string that might contain other characters
 * Useful for parsing IDs from mixed content
 *
 * @param {string} str - String that might contain a UUID
 * @returns {string|null} The extracted UUID or null
 *
 * @example
 * extractUUID('prefix-550e8400-e29b-41d4-a716-446655440000-suffix')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 */
export function extractUUID(str) {
  if (!str || typeof str !== 'string') return null;

  const match = str.match(UUID_REGEX);
  return match ? match[0] : null;
}

/**
 * Generate a simple unique ID (not cryptographically secure)
 * For temporary IDs that don't need to persist to database
 *
 * @param {string} prefix - Optional prefix (default: 'id')
 * @returns {string} Generated ID like "id_a1b2c3d4"
 */
export function generateTempId(prefix = 'id') {
  const random = Math.random().toString(36).substring(2, 10);
  return `${prefix}_${random}`;
}

/**
 * Generate a timestamped ID
 * Useful for sorting by creation order
 *
 * @param {string} prefix - Optional prefix
 * @returns {string} Generated ID like "prefix_1704067200000_a1b2"
 */
export function generateTimestampedId(prefix = 'id') {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 6);
  return `${prefix}_${timestamp}_${random}`;
}

// ============================================================================
// WINDOW EXPORTS (for non-module usage)
// ============================================================================

if (typeof window !== 'undefined') {
  window.UuidUtils = {
    UUID_REGEX,
    isValidUUID,
    isDatabaseId,
    extractUUID,
    generateTempId,
    generateTimestampedId
  };
}

export default {
  UUID_REGEX,
  isValidUUID,
  isDatabaseId,
  extractUUID,
  generateTempId,
  generateTimestampedId
};
